import EmojiBlackSvg from "@/assets/svg/EmojiBlackSvg";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useCreatePostCommentMutation } from "@/store/api/postCommentApi";
import { Avatar, Box, Button, ClickAwayListener, Stack, Tooltip, Typography } from "@mui/material";
import {
  Editor,
  EditorState,
  CompositeDecorator,
  Modifier,
  DraftHandleValue,
  ContentState,
} from "draft-js";
import EmojiPicker from "emoji-picker-react";
import { FC, useEffect, useRef, useState } from "react";
import { styles } from "./stylesCommentEditor";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import {
  addPendingComment,
  removePendingComment,
  setRetryText,
  updatePendingCommentStatus,
  updatePendingCommentUid,
} from "@/store/slice/commentPendingSlice";
import theme from "@/theme";

interface CommentEditorProps {
  editorState: EditorState;
  edit: any;
  replyUid?: string;
  setEditorState: (state: EditorState) => void;
  showPicker: boolean;
  postId?: string;
  setReplyUid: any;
  setShowPicker: React.Dispatch<React.SetStateAction<boolean>>;
  handleEmojiClick: (emoji: any) => void;
  maxLength?: number;
  placeholder?: string;
  onSubmit?: () => void;
  variant?: "comment" | "reply" | "edit";
  onCommentCreated: () => void;
  level: any;
  isLiked: boolean;
}

const CommentEditor: FC<CommentEditorProps> = ({
  setReplyUid,
  replyUid,
  postId,
  editorState: editorStateProp,
  setEditorState: setEditorStateProp,
  showPicker,
  setShowPicker,
  handleEmojiClick,
  maxLength,
  placeholder = "Tell something...",
  onSubmit,
  onCommentCreated,
  level,
  isLiked,
}) => {
  const dispatch = useAppDispatch();
  const [createPostComment] = useCreatePostCommentMutation();
  const { user } = useAppSelector((state) => state.auth);
  const [error, setError] = useState<string | null>(null);
  // const [charCount, setCharCount] = useState(maxLength || 1000);
  const retryText = useAppSelector((state) => state.commentPending.retryText);
  const [enteredChars, setEnteredChars] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const emojiButtonRef = useRef<HTMLDivElement | null>(null);
  const [pickerPosition, setPickerPosition] = useState({
    top: "100%",
    left: "0px",
    bottom: "auto",
    right: "auto",
  });

  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";
  // console.log(charCount);
  const MentionSpan = (props: any) => (
    <span
      style={{
        color: theme.palette.custom.mainBlue,
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: "15px",
        lineHeight: "24px",
        letterSpacing: "-1%",
      }}
    >
      {props.children}
    </span>
  );

  const findMentionEntities = (contentBlock: any, callback: any) => {
    const text = contentBlock.getText();
    const regex = /@\S+/g;
    let matchArr;
    while ((matchArr = regex.exec(text)) !== null) {
      const start = matchArr.index;
      callback(start, start + matchArr[0].length);
    }
  };

  const decorator = new CompositeDecorator([
    {
      strategy: findMentionEntities,
      component: MentionSpan,
    },
  ]);

  const editorState = editorStateProp;
  const setEditorState = setEditorStateProp;
  const disabled =
    !editorState.getCurrentContent().hasText() ||
    (maxLength !== undefined && !!error) ||
    (!hasPremium && !isLiked);

  const sendComment = async () => {
    const text = editorState.getCurrentContent().getPlainText().trim();
    if (!text || !postId || !user) return;
    const tempUid = crypto.randomUUID();
    let serverUid: string | null = null;
    dispatch(
      addPendingComment({
        uid: tempUid,
        text,
        ...(replyUid ? { replyToUid: replyUid } : {}),
        level: level,
      })
    );
    const emptyState = EditorState.createEmpty(decorator);
    setEditorState(emptyState);
    setEditorStateProp(emptyState);
    setReplyUid("");
    if (onSubmit) {
      onSubmit();
    }
    try {
      const res = await createPostComment({
        content: text,
        postUid: postId,
        ...(replyUid ? { replyToUid: replyUid } : {}),
      }).unwrap();

      serverUid = res.uid;

      dispatch(
        updatePendingCommentUid({
          oldUid: tempUid,
          newUid: res.uid,
        })
      );

      dispatch(
        updatePendingCommentStatus({
          uid: res.uid,
          status: "published",
        })
      );

      onCommentCreated?.();
    } catch (err: any) {
      console.error("Failed to send comment", err);
      setError(err?.data?.message?.[0] || "Something went wrong");

      dispatch(
        updatePendingCommentStatus({
          uid: tempUid,
          status: "error",
        })
      );
    } finally {
      if (!serverUid) return;

      setTimeout(() => dispatch(removePendingComment({ uid: serverUid || "" })), 5000);
    }
  };

  useEffect(() => {
    if (retryText) {
      const contentState = ContentState.createFromText(retryText.text);
      const newEditorState = EditorState.createWithContent(contentState, decorator);
      setEditorState(newEditorState);
      setEditorStateProp(newEditorState);

      dispatch(removePendingComment({ uid: retryText.uid }));

      dispatch(setRetryText(undefined));
    }
  }, [retryText]);

  const handleChangeEditor = (state: EditorState) => {
    const textLength = state.getCurrentContent().getPlainText().length;

    if (maxLength) {
      const remaining = maxLength - textLength;
      // setCharCount(remaining >= 0 ? remaining : 0);
      setError(
        remaining >= 0 ? null : `Content must be shorter than or equal to ${maxLength} characters`
      );
    }
    setEnteredChars(textLength);
    setEditorState(state);
    setEditorStateProp(state);
  };

  const handleInsertText = (
    textToInsert: string,
    state: EditorState,
    setEditorState: (s: EditorState) => void,
    setEditorStateProp: (s: EditorState) => void,
    maxLength?: number,
    setError?: (msg: string | null) => void,
    setEnteredChars?: (count: number) => void
  ): DraftHandleValue => {
    const content = state.getCurrentContent();
    const selection = state.getSelection();

    const newContent = Modifier.insertText(content, selection, textToInsert);
    const newEditorState = EditorState.push(state, newContent, "insert-characters");

    setEditorState(newEditorState);
    setEditorStateProp(newEditorState);
    const textLength = newContent.getPlainText().length;
    if (setEnteredChars) setEnteredChars(textLength);
    if (maxLength) {
      const textLength = newContent.getPlainText().length;
      const remaining = maxLength - textLength;
      if (setError) {
        setError(
          remaining >= 0 ? null : `Content must be shorter than or equal to ${maxLength} characters`
        );
      }
      // setCharCount(remaining >= 0 ? remaining : 0);
    }

    return "handled";
  };

  const remainingChars = maxLength ? maxLength - enteredChars : undefined;
  const isOverLimit = remainingChars !== undefined && remainingChars < 0;

  const togglePicker = () => {
    if (emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceRight = window.innerWidth - rect.right;

      setPickerPosition({
        top: spaceBelow >= 400 ? "100%" : "auto",
        bottom: spaceBelow >= 400 ? "auto" : "0px",
        left: spaceBelow >= 400 ? "0px" : spaceRight >= 400 ? "100%" : "auto",
        right: spaceBelow >= 400 ? "auto" : spaceRight >= 400 ? "auto" : "100%",
      });
    }

    setShowPicker((prev) => !prev);
  };

  return (
    <Stack sx={styles.wrapper}>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "0.75rem", flex: 1 }}>
        <Avatar src={user?.image?.url || ""} alt="Profile" sx={styles.avatar} />
        <Box sx={styles.editorContainer}>
          <Box sx={styles.editorWrapper} className="hide-scrollbar">
            <Editor
              editorState={editorState}
              onChange={handleChangeEditor}
              placeholder={placeholder}
              customStyleMap={{}}
              onFocus={handleFocus}
              onBlur={handleBlur}
              handlePastedText={(pastedText, _html, state): DraftHandleValue =>
                handleInsertText(
                  pastedText,
                  state,
                  setEditorState,
                  setEditorStateProp,
                  maxLength,
                  setError,
                  setEnteredChars
                )
              }
              spellCheck={true}
            />
          </Box>
          <Stack sx={styles.bottomControls} direction="row">
            <Box ref={emojiButtonRef} display="inline-block" position="relative">
              <EmojiBlackSvg onClick={togglePicker} cursor="pointer" />

              {showPicker && (
                <ClickAwayListener onClickAway={() => setShowPicker(false)}>
                  <Box
                    sx={{
                      position: "absolute",
                      zIndex: 10,
                      height: "363px",
                      overflow: "hidden",
                      ...pickerPosition,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} height={363} />
                  </Box>
                </ClickAwayListener>
              )}
            </Box>

            {isFocused && (
              <Box
                sx={{
                  display: "flex",
                  height: 24,
                  gap: "10px",
                  justifyContent: "center",
                  margin: "0 20px 0 auto",
                }}
              >
                {isOverLimit ? (
                  <>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "13px",
                        lineHeight: "24px",
                        letterSpacing: "-1%",
                        color: "#CE212BB2",
                      }}
                    >
                      Text must be {maxLength} characters or less
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontStyle: "Semi Bold",
                        fontSize: "13px",
                        lineHeight: "24px",
                        letterSpacing: "-1%",
                        color: theme.palette.custom.secondaryRed,
                      }}
                    >
                      {remainingChars}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontStyle: "Semi Bold",
                      fontSize: "13px",
                      lineHeight: "24px",
                      letterSpacing: "-1%",
                      color: "#7D8690",
                    }}
                  >
                    {remainingChars}
                  </Typography>
                )}
              </Box>
            )}
            <Tooltip
              title={
                !hasPremium && !isLiked ? (
                  <Box sx={styles.TooltipWrapper}>
                    <Typography sx={styles.TooltipText}>
                      You can send a Comment only after you like a post.
                    </Typography>
                  </Box>
                ) : (
                  ""
                )
              }
              placement="top"
              disableHoverListener={hasPremium}
              componentsProps={{
                tooltip: {
                  sx: styles.TooltipProps,
                },
              }}
            >
              <span>
                <Button sx={styles.sendButton} onClick={sendComment} disabled={disabled}>
                  Send
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
};

export default CommentEditor;
